import React from 'react';
import { reportClientError } from '../utils/errorReporting';

// Límite de errores de React. Captura los fallos de render de su subárbol,
// los reporta al backend y muestra una pantalla de recuperación en vez de
// dejar la app en blanco.
//
// Uso:
//   <ErrorBoundary name="app">...</ErrorBoundary>
//   <ErrorBoundary name="vista" resetKey={currentView} compact>...</ErrorBoundary>
// resetKey: al cambiar de valor, el boundary se rearma solo (p. ej. al
// navegar a otra vista).
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientError(error, {
      source: 'error-boundary',
      componentStack: info && info.componentStack,
      context: this.props.name || '',
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleReload = () => window.location.reload();

  handleDismiss = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.compact) {
      return (
        <div className="m-4 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm">
          <p className="font-bold text-red-800">Esta sección ha fallado</p>
          <p className="mt-1 text-red-600">{error.message || 'Error inesperado'}</p>
          <div className="mt-3 flex gap-2">
            <button onClick={this.handleDismiss}
              className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-700 font-semibold hover:bg-red-100">
              Reintentar
            </button>
            <button onClick={this.handleReload}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">
              Recargar la app
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border p-6 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="text-lg font-bold text-gray-900">Algo ha fallado</h1>
          <p className="mt-1.5 text-sm text-gray-500">
            La aplicación ha encontrado un error inesperado y no puede continuar.
            El equipo técnico ya ha recibido el aviso.
          </p>
          <button onClick={this.handleReload}
            className="mt-4 w-full py-3 rounded-2xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors">
            Recargar la aplicación
          </button>
          {error.message && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-gray-400 cursor-pointer">Detalles técnicos</summary>
              <pre className="mt-2 text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap">
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
